import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * React Error Boundary — catches render errors from any descendant component
 * and shows a user-friendly fallback instead of a blank screen.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomePage />
 *   </ErrorBoundary>
 *
 * An optional `fallback` prop overrides the default UI.
 * An optional `onReset` prop is called when the user dismisses the error.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console — replace with a real error-reporting service (Sentry etc.) when available
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
    if (this.props.onError) {
      this.props.onError(error, info);
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Allow a custom fallback UI
    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        p={3}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            An unexpected error occurred. The team has been notified.
            Try refreshing the page or go back to the home screen.
          </Typography>
          <Box display="flex" gap={2} justifyContent="center">
            <Button variant="outlined" onClick={this.handleReset}>
              Try again
            </Button>
            <Button variant="contained" href="/">
              Go home
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
